import { makeObservable, observable } from "mobx";
import { addressFormToCreateData, addressFormToUpdateData } from "helpers/forms/address";
import type { AddressFormValue } from "helpers/forms/addressTypes";
import { type AddressClient, AddressKey } from "services/address/addressClient";
import { type AddressData } from "services/address/addressTypes";
import { type CompanyStore } from "services/company/companyStore";
import type { CompanyData } from "services/company/companyTypes";
import type { CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import {
  CompanyProduct,
  VirtualOfficeBillingPeriod,
  VirtualOfficeRegisterEnum,
} from "services/companyProducts/companyProductsTypes";
import type { IPricingPlanStore } from "services/pricingPlan/pricingPlanStoreBase";
import { FormError } from "types/errors";
import type { FormSubmitResult } from "types/form";

type UpdateAddressData =
  | {
      virtualOfficeBillingPeriod?: VirtualOfficeBillingPeriod;
    }
  | {
      address?: AddressFormValue;
      useHomeAddressAsRegistered?: boolean;
    };

export abstract class CompanyAddressStoreBase {
  private _address?: AddressData = undefined;

  protected constructor(
    protected readonly _companyStore: CompanyStore,
    protected readonly _addressClient: AddressClient,
    protected readonly _companyProductStore: CompanyProductsStore,
    protected readonly _pricingPlanStore: IPricingPlanStore,
  ) {
    makeObservable(this, {
      _address: observable,
    } as any);

    // some pricing plan includes virtual office, so we have to check on every update should we turn oi on or not
    // reaction(() => [this._companyStore.company?.pricingPlan], this._onPricingPlanChange.bind(this));
  }

  get address(): undefined | AddressData {
    return this._address;
  }

  set address(value: undefined | AddressData) {
    this._address = value;
  }

  async init(): Promise<void> {
    const company = this._companyStore.company;
    if (company) {
      this.address = await this._addressClient.getAddress(AddressKey.Company, company.id);
    }
  }

  async updateAddress(data: UpdateAddressData): Promise<FormSubmitResult> {
    let address, virtualOfficeBillingPeriod, useHomeAddressAsRegistered;

    if ("address" in data) {
      address = data.address;
    }

    if ("virtualOfficeBillingPeriod" in data) {
      virtualOfficeBillingPeriod = data.virtualOfficeBillingPeriod || VirtualOfficeBillingPeriod.Yearly; // default to yearly
    }

    if (virtualOfficeBillingPeriod) {
      const isVirtualOfficeIncluded = this._pricingPlanStore.isProductIncluded(CompanyProduct.VirtualOffice);

      if (!isVirtualOfficeIncluded) {
        await this._companyProductStore.addProduct(CompanyProduct.VirtualOffice, virtualOfficeBillingPeriod);
      }

      await this._updateAddress(true);
    } else if (address || ("useHomeAddressAsRegistered" in data && data.useHomeAddressAsRegistered)) {
      if ("useHomeAddressAsRegistered" in data) {
        useHomeAddressAsRegistered = data.useHomeAddressAsRegistered;
      }
      await this._removeVirtualOfficeAndUpdateAddress(address, useHomeAddressAsRegistered);
    } else {
      throw new FormError("Please select address");
    }
  }

  async updateRegisteredVirtualOffice(virtualOffice: VirtualOfficeRegisterEnum): Promise<void> {
    const addressConfig = {
      [VirtualOfficeRegisterEnum.RegisteredOffice]: { useVirtualOffice: true, useHomeAddress: false },
      [VirtualOfficeRegisterEnum.HomeOffice]: { useVirtualOffice: false, useHomeAddress: true },
      [VirtualOfficeRegisterEnum.AlternateOffice]: { useVirtualOffice: false, useHomeAddress: false },
    };

    const config = addressConfig[virtualOffice];
    if (!config) {
      throw new Error(`Invalid virtual office request: ${virtualOffice}`);
    }

    await this._updateAddress(config.useVirtualOffice, undefined, config.useHomeAddress);
  }

  private async _removeVirtualOfficeAndUpdateAddress(
    address?: AddressFormValue,
    useHomeAddressAsRegistered: boolean = false,
  ): Promise<void> {
    const company = this._companyStore.getCompany();
    const changeFromVirtual = Boolean(company.useVirtualOffice);

    // Save the new address first so that if it fails (e.g. postcode validation),
    // we don't remove the virtual office product or update company info.
    let savedAddress: AddressData | undefined;
    if (address) {
      const currentAddress = await this._addressClient.getAddress(AddressKey.Company, company.id);
      savedAddress = await this._createOrUpdateAddress(changeFromVirtual, company, address, currentAddress);
    }

    // Address saved successfully (or using home address) — now safe to remove product and update company info
    if (this._companyProductStore.hasProduct(CompanyProduct.VirtualOffice)) {
      await this._companyProductStore.deleteProduct(CompanyProduct.VirtualOffice);
    }

    await this._companyStore.updateVirtualOffice(false, useHomeAddressAsRegistered);

    if (savedAddress) {
      this.address = savedAddress;
    } else if (useHomeAddressAsRegistered) {
      this.address = await this._addressClient.getAddress(AddressKey.Company, company.id);
    }
  }

  protected async _updateAddress(
    useVirtualOffice: boolean,
    address?: AddressFormValue,
    useHomeAddressAsRegistered: boolean = false,
  ): Promise<void> {
    const company = this._companyStore.getCompany();
    const changeFromVirtual = Boolean(company.useVirtualOffice) && !useVirtualOffice;

    await this._companyStore.updateVirtualOffice(useVirtualOffice, useHomeAddressAsRegistered);

    // We need to update address after setting useVirtualOffice because in case of
    // useVirtualOffice=true it will be changed to Hoxton address and in case of
    // useVirtualOffice=false existed address will be deleted
    const updatedAddress = await this._addressClient.getAddress(AddressKey.Company, company.id);
    // set address to Hoxton address if useVirtualOffice is true
    // or if useHomeAddressAsRegistered is true and address is not provided
    if (useVirtualOffice || (useHomeAddressAsRegistered && !address)) {
      this.address = updatedAddress;
      return;
    }

    // if address is not provided, skip the address update
    if (address) {
      this.address = await this._createOrUpdateAddress(changeFromVirtual, company, address, updatedAddress);
    }
  }

  protected async _createOrUpdateAddress(
    changeFromVirtual: boolean,
    company: CompanyData,
    newAddress: AddressFormValue,
    currentAddress?: AddressData,
  ): Promise<AddressData> {
    // update or create address in address service
    // odd logic about when use create or update
    const handler =
      changeFromVirtual || !currentAddress
        ? async () =>
            await this._addressClient.createAddress(addressFormToCreateData(newAddress, { companyId: company.id }))
        : async () => await this._addressClient.updateAddress(currentAddress.id, addressFormToUpdateData(newAddress));
    return await handler();
  }
}
